import _ from "lodash";
import { columnSchema, computedColumns, tableHeaders } from "../Schemas/MedicalEncounterClaim";
import { OriginalData } from "../Schemas/OriginalData";
import DataFormatter from "../../DataFormatter";
import isValidMedicalEncounterClaimField, {
  crossFieldErrors,
} from "../Validators/IsValidMedicalEncounterClaimField";
import { isValidClaimNumber as isValidClaimNumberFormat } from "../Validators/DataValidations";
import {
  isBlockedPlaceOfService,
  isBlockedHcpcsCode,
  isBlockedDiagnosisCode,
  ENCOUNTER_DIAGNOSIS_CODE_FIELDS,
} from "../BlockedRowFilter";

const MIN_BATCH_SIZE = 100;

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 0));

const MedicalEncounterClaimsEngine = async ({
  data,
  mapping,
  ndcList,
  accessibleEntityIds = [],
  salt,
  filename,
  onProgress,
}) => {
  var hasErrors = false;
  var noMappingError = false;
  var missingMappedColumns = [];
  var columnsWithErrors = [];
  var result = { originalData: [], processedData: [] };

  const ndcMap = new Map(ndcList.map((ndc) => [ndc.code, ndc]));
  const accessibleEntitySet = new Set(accessibleEntityIds ?? []);
  const deIdentifiedUiColumns = ["claim_number"];

  const process = async () => {
    if (!mapping) {
      _.each(data, (row) => {
        result.originalData.push(new OriginalData(row, mapping, false));
      });

      noMappingError = true;
      hasErrors = true;

      return result;
    }

    checkMappingMismatch();

    const summary = buildEmptySummary(data.length);

    const BATCH_SIZE = Math.max(MIN_BATCH_SIZE, Math.floor(data.length / 100));

    for (let i = 0; i < data.length; i++) {
      if (i > 0 && i % BATCH_SIZE === 0) {
        if (onProgress) onProgress(i, data.length);
        await yieldToMain();
      }

      const row = data[i];

      if (!inNdcList(row)) {
        result.originalData.push(new OriginalData(row, mapping, false));
        // ENTITY FILTERING DISABLED
        // Every NDC-eligible row is now selected, whatever entity it belongs to.
        //
        // } else if (!inAccessibleEntities(row)) {
        //   // Row is for an entity the user does not have access to: exclude it from
        //   // the submittable/valid set and surface it in the excluded-entities summary.
        //   result.originalData.push(new OriginalData(row, mapping, false));
        //   includeInExcludedSummary(row, summary);
      } else if (isBlockedRow(row)) {
        result.originalData.push(new OriginalData(row, mapping, false));
        summary.blockedRowCount += 1;
      } else {
        const { claim, originalData } = processRow(row, mapping, i);

        result.processedData.push(claim);
        result.originalData.push(originalData);
        hasErrors = hasErrors || claim.hasErrors;
        includeInSummary(claim, summary);
      }
    }

    summary.rows = result.processedData.length;

    return { ...result, summary };
  };

  const checkMappingMismatch = () => {
    if (!data.length) return;

    _.forIn(mapping, (value, key) => {
      if (value && !(value in data[0])) {
        missingMappedColumns.push({
          schemaField: key,
          mappedColumn: value,
        });
      }
    });
  };

  const inNdcList = (row) => {
    return ndcMap.has(row[mapping["ndc"]]);
  };

  const isBlockedRow = (row) => {
    return (
      isBlockedPlaceOfService(row[mapping["encounter_place_of_service_code"]]) ||
      isBlockedHcpcsCode(row[mapping["hcpcs_code"]]) ||
      isBlockedHcpcsCode(row[mapping["encounter_hcpcs_code"]]) ||
      ENCOUNTER_DIAGNOSIS_CODE_FIELDS.some((field) => isBlockedDiagnosisCode(row[mapping[field]]))
    );
  };

  // ENTITY FILTERING DISABLED
  //
  // // A row is excluded for "wrong entity" only when it has a present entity id
  // // that the user does not have access to. A blank/unmapped entity id is left to
  // // normal processing so the required-field validation flags it instead.
  // const inAccessibleEntities = (row) => {
  //   const id_340b = row[mapping["id_340b_raw"]]?.trim();
  //   if (!id_340b) return true;
  //   return accessibleEntitySet.has(id_340b);
  // };

  const processRow = (row, mapping, index) => {
    const trimmedRow = trimRow(row, mapping);
    const originalData = new OriginalData(row, mapping, true);

    const { manufacturer, product_name } = getManufacturerAndProductName(
      trimmedRow[mapping["ndc"]]
    );

    const claim = {
      hasErrors: false,
      data: { ...columnSchema },
      rawData: {},
      columns_with_errors: {},
      validations: [],
      manufacturer,
      product_name,
      line_number: index + 1,
      filename,
    };

    _.forIn(claim.data, (_, columnName) => {
      setClaimData(trimmedRow, claim, columnName, mapping, salt);
    });

    claim.esp_id = DataFormatter.medicalEspId(
      claim.data.date_of_service,
      claim.data.formatted_claim_number,
      claim.data.claim_number,
      claim.data.ndc,
      claim.data.service_provider_id
    );

    _.forIn(mapping, (sourceCol, schemaKey) => {
      claim.rawData[schemaKey] = trimmedRow[sourceCol] ?? "";
    });

    setValidationErrors(trimmedRow, claim, originalData, mapping);
    setCrossFieldErrors(claim, originalData);

    return { claim, originalData };
  };

  const trimRow = (row, mapping) => {
    const selectedData = _.pick(row, _.values(mapping));
    return _.mapValues(selectedData, (val) => val?.trim());
  };

  const setClaimData = (row, claim, columnName, mapping, salt) => {
    claim.data[columnName] = formatValue(columnName, row[mapping[columnName]], salt, row, mapping);
  };

  const setValidationErrors = (row, claim, originalData, mapping) => {
    _.forEach(mapping, (value, key) => {
      let result = isValidMedicalEncounterClaimField(row[value], key, row, mapping);

      // add validation error for hashed columns if salt could not be retrieved
      if (!salt && deIdentifiedUiColumns.includes(key)) {
        result = { isValid: false, message: "Data could not be de-identified" };
      }

      if (!result.isValid) {
        claim.validations.push({
          field: key,
          message: result.message,
          originalValue: row[value] ?? "",
          processedValue: claim.data[key] ?? "",
        });
        claim.columns_with_errors[key] = true;
        claim.hasErrors = true;
        originalData.columns_with_errors[key] = true;
        originalData.hasErrors = true;
        columnsWithErrors.push(key);
      }
    });
  };

  const setCrossFieldErrors = (claim, originalData) => {
    const errors = crossFieldErrors(claim.data);
    _.forEach(errors, (error) => {
      claim.validations.push({
        field: error.field,
        message: error.message,
        originalValue: claim.data[error.field] ?? "",
        processedValue: claim.data[error.field] ?? "",
      });
      claim.columns_with_errors[error.field] = true;
      claim.hasErrors = true;
      originalData.columns_with_errors[error.field] = true;
      originalData.hasErrors = true;
      columnsWithErrors.push(error.field);
    });
  };

  const normalizeClaimNumber = (value) => {
    if (!value) return null;

    const claimNumber = value.replace(/-/g, "").toUpperCase();
    if (isValidClaimNumberFormat(claimNumber)) {
      return DataFormatter.removeLeadingZeros(claimNumber);
    }
    return null;
  };

  const formatValue = (schemaColumnName, value, salt, row, mapping) => {
    if (["date_of_service", "encounter_date_of_service"].includes(schemaColumnName)) {
      return DataFormatter.formatDate(value);
    }

    if (schemaColumnName === "claim_number") {
      return DataFormatter.generateSecureHash(value, salt);
    }

    if (schemaColumnName === "formatted_claim_number") {
      return DataFormatter.generateSecureHash(
        normalizeClaimNumber(row[mapping["claim_number"]]),
        salt
      );
    }

    return value;
  };

  const getManufacturerAndProductName = (item) => {
    const found = ndcMap.get(item);
    return { manufacturer: found?.manufacturer, product_name: found?.product_name };
  };

  const includeInSummary = (claim, summary) => {
    const {
      manufacturer,
      product_name,
      line_number,
      hasErrors: rowHasErrors,
      validations,
      data,
    } = claim;
    const ndc = DataFormatter.normalizeNdc(data["ndc"]);
    const id_340b = data["id_340b_raw"];
    const service_provider_id = data["service_provider_id"];

    summary.valid += rowHasErrors ? 0 : 1;
    summary.invalid += rowHasErrors ? 1 : 0;

    summary._products.add(ndc);
    summary._pharmacies.add(service_provider_id);

    summary.errors.push({ line_number, validations });

    validations.forEach((validation) => {
      const key = `${validation.field}-${validation.message}`;
      const previousValidation = summary.validations[key];
      if (previousValidation) {
        previousValidation.line_numbers.push(line_number);
      } else {
        summary.validations[key] = {
          field: validation.field,
          message: validation.message,
          line_numbers: [line_number],
        };
      }
    });

    let manufacturer_stats = summary.manufacturers[manufacturer];
    if (!manufacturer_stats) {
      manufacturer_stats = {
        rows: 0,
        valid: 0,
        invalid: 0,
        _pharmacies: new Set(),
        _products: new Set(),
        _ndcs: new Set(),
        hasErrors: false,
      };
      summary.manufacturers[manufacturer] = manufacturer_stats;
    }
    manufacturer_stats.rows += 1;
    manufacturer_stats.valid += rowHasErrors ? 0 : 1;
    manufacturer_stats.invalid += rowHasErrors ? 1 : 0;
    manufacturer_stats._pharmacies.add(service_provider_id);
    manufacturer_stats._products.add(product_name);
    manufacturer_stats._ndcs.add(ndc);
    manufacturer_stats.hasErrors = manufacturer_stats.hasErrors || rowHasErrors;

    let entity_summary = summary._entitiesMap.get(id_340b);

    if (entity_summary) {
      const product_stats = entity_summary.ndcs[ndc];
      entity_summary.ndcs[ndc] = {
        rows: (product_stats?.rows || 0) + 1,
        ndc: ndc,
        product_name: product_name,
        manufacturer: manufacturer,
      };
      entity_summary._products.add(product_name);
      entity_summary._manufacturers.add(manufacturer);
      entity_summary.rowCount += 1;
    } else {
      entity_summary = {
        id_340b: id_340b,
        rowCount: 1,
        ndcs: {
          [ndc]: {
            rows: 1,
            ndc: ndc,
            product_name: product_name,
            manufacturer: manufacturer,
          },
        },
        _products: new Set([product_name]),
        _manufacturers: new Set([manufacturer]),
      };
      summary._entitiesMap.set(id_340b, entity_summary);
    }
  };

  // ENTITY FILTERING DISABLED retained but unreached; the excluded-entities summary
  // stays empty while the entity gate in process() is commented out.
  //
  // const includeInExcludedSummary = (row, summary) => {
  //   const trimmedRow = trimRow(row, mapping);
  //   const rawNdc = trimmedRow[mapping["ndc"]];
  //   const ndc = DataFormatter.normalizeNdc(rawNdc);
  //   const id_340b = trimmedRow[mapping["id_340b_raw"]];
  //   const { manufacturer, product_name } = getManufacturerAndProductName(rawNdc);

  //   summary.excludedRowCount += 1;

  //   let entity_summary = summary._excludedEntitiesMap.get(id_340b);

  //   if (entity_summary) {
  //     const product_stats = entity_summary.ndcs[ndc];
  //     entity_summary.ndcs[ndc] = {
  //       rows: (product_stats?.rows || 0) + 1,
  //       ndc: ndc,
  //       product_name: product_name,
  //       manufacturer: manufacturer,
  //     };
  //     entity_summary._products.add(product_name);
  //     entity_summary._manufacturers.add(manufacturer);
  //     entity_summary.rowCount += 1;
  //   } else {
  //     entity_summary = {
  //       id_340b: id_340b,
  //       rowCount: 1,
  //       ndcs: {
  //         [ndc]: {
  //           rows: 1,
  //           ndc: ndc,
  //           product_name: product_name,
  //           manufacturer: manufacturer,
  //         },
  //       },
  //       _products: new Set([product_name]),
  //       _manufacturers: new Set([manufacturer]),
  //     };
  //     summary._excludedEntitiesMap.set(id_340b, entity_summary);
  //   }
  // };

  const buildEmptySummary = (originalCount) => ({
    original: originalCount,
    rows: 0,
    valid: 0,
    invalid: 0,
    excludedRowCount: 0,
    blockedRowCount: 0,
    manufacturers: {},
    _pharmacies: new Set(),
    _products: new Set(),
    _entitiesMap: new Map(),
    _excludedEntitiesMap: new Map(),
    errors: [],
    validations: {},
  });

  const finalizeSummary = (summary) => {
    summary.pharmacies = [...summary._pharmacies];
    summary.products = [...summary._products];
    summary.entities = [...summary._entitiesMap.values()].map((e) => ({
      ...e,
      products: [...e._products],
      manufacturers: [...e._manufacturers],
    }));
    summary.excludedEntities = [...summary._excludedEntitiesMap.values()].map((e) => ({
      ...e,
      products: [...e._products],
      manufacturers: [...e._manufacturers],
    }));
    Object.values(summary.manufacturers).forEach((m) => {
      m.pharmacies = [...m._pharmacies];
      m.products = [...m._products];
      m.ndcs = [...m._ndcs];
      delete m._pharmacies;
      delete m._products;
      delete m._ndcs;
    });
    delete summary._pharmacies;
    delete summary._products;
    delete summary._entitiesMap;
    delete summary._excludedEntitiesMap;
    return summary;
  };

  const processed = await process();

  return {
    data: { originalData: processed.originalData, processedData: processed.processedData },
    summary: processed.summary
      ? finalizeSummary(processed.summary)
      : buildEmptySummary(data.length),
    hasErrors,
    noMappingError,
    missingMappedColumns,
    columnNames: [...tableHeaders, ...computedColumns],
    columnsWithErrors: _.uniq(columnsWithErrors),
  };
};

export default MedicalEncounterClaimsEngine;
